import { useState, useEffect } from 'react';
import {
  Badge,
  Button,
  Spinner,
} from '@rnd-payplus/alloy-react';
import { KPICard } from '@rnd-payplus/alloy-react/patterns';
import {
  CheckCircle,
  AlertCircle,
  RefreshCw,
} from 'lucide-react';
import { usePageShell } from '../contexts/PageShellContext';

interface Service {
  name: string;
  url: string;
  status: 'healthy' | 'unhealthy';
  responseTime: number;
  port: number;
}

interface ConnectionPool {
  platform: string;
  tenant: string;
  active: number;
  idle: number;
  total: number;
}

const ServicesHealthPage = () => {
  const [services, setServices] = useState<Service[]>([]);
  const [pools, setPools] = useState<ConnectionPool[]>([]);
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);

  const checkServices = async () => {
    try {
      // Fetch from backend API instead of checking each service directly
      const response = await fetch('/api/services/health');
      const services = await response.json();
      setServices(services);
    } catch (error) {
      console.error('Error fetching service health:', error);
    }

    // Fetch real connection pool stats
    try {
      const poolRes = await fetch('/api/db/pool-stats');
      if (poolRes.ok) {
        const poolData = await poolRes.json();
        setPools([{
          platform: 'PostgreSQL',
          tenant: 'all',
          active: poolData.active,
          idle: poolData.idle,
          total: poolData.total,
        }]);
      }
    } catch (err) {
      console.warn('Pool stats unavailable:', err);
    }

    setLoading(false);
  };

  useEffect(() => {
    checkServices();
    if (autoRefresh) {
      const interval = setInterval(checkServices, 5000);
      return () => clearInterval(interval);
    }
  }, [autoRefresh]);

  const healthyCount = services.filter(s => s.status === 'healthy').length;
  const totalConnections = pools.reduce((sum, p) => sum + p.active, 0);

  usePageShell({
    title: 'Services Health Monitor',
    secondaryActions: (
      <>
        <Button
          variant={autoRefresh ? 'default' : 'outline'}
          onClick={() => setAutoRefresh(!autoRefresh)}
        >
          Auto-Refresh: {autoRefresh ? 'ON' : 'OFF'}
        </Button>
        <Button variant="outline" onClick={checkServices}>
          <RefreshCw className="mr-2 h-4 w-4" />
          Refresh Now
        </Button>
      </>
    ),
    breadcrumbs: [
      { label: 'Home', href: '/dashboard' },
      { label: 'Services Health' },
    ],
  });

  return (
    <div>
      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-6">
        <KPICard
          title="Healthy Services"
          value={`${healthyCount}/${services.length}`}
        />
        <KPICard
          title="Active Connections"
          value={totalConnections}
        />
        <KPICard
          title="Avg Response Time"
          value={`${Math.round(services.reduce((sum, s) => sum + s.responseTime, 0) / services.length) || 0}ms`}
        />
      </div>

      {/* Services Status */}
      <div className="mb-5">
        <h2 className="text-base font-medium leading-6 mb-4">Microservices Status</h2>
        {loading ? (
            <div className="flex justify-center p-4">
              <Spinner />
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {services.map((service) => (
                <div key={service.name} className="rounded-lg border bg-card p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium">{service.name}</span>
                    {service.status === 'healthy' ? (
                      <CheckCircle className="h-5 w-5 text-green-500" />
                    ) : (
                      <AlertCircle className="h-5 w-5 text-red-500" />
                    )}
                  </div>
                  <div className="mb-2">
                    <Badge
                      variant={service.status === 'healthy' ? 'success' : 'destructive'}
                    >
                      {service.status}
                    </Badge>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Port: {service.port}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Response: {service.responseTime}ms
                  </p>
                </div>
              ))}
            </div>
        )}
      </div>

      {/* Connection Pools */}
      <div>
        <h2 className="text-base font-medium leading-6 mb-4">Connection Pools</h2>
        <div className="overflow-x-auto rounded-md border">
            <table className="w-full text-sm">
              <thead className="bg-muted/50">
                <tr>
                  <th className="px-4 py-2 text-left font-medium">Platform</th>
                  <th className="px-4 py-2 text-left font-medium">Tenant</th>
                  <th className="px-4 py-2 text-left font-medium">Active</th>
                  <th className="px-4 py-2 text-left font-medium">Idle</th>
                  <th className="px-4 py-2 text-left font-medium">Total</th>
                  <th className="px-4 py-2 text-left font-medium">Utilization</th>
                </tr>
              </thead>
              <tbody>
                {pools.map((pool, idx) => {
                  const utilization = (pool.active / pool.total) * 100;
                  return (
                    <tr key={idx} className="border-t">
                      <td className="px-4 py-2">{pool.platform}</td>
                      <td className="px-4 py-2">{pool.tenant}</td>
                      <td className="px-4 py-2">{pool.active}</td>
                      <td className="px-4 py-2">{pool.idle}</td>
                      <td className="px-4 py-2">{pool.total}</td>
                      <td className="px-4 py-2">
                        <div className="flex items-center gap-2">
                          <div className="flex-grow h-2 rounded-full bg-muted overflow-hidden">
                            <div
                              className={`h-full rounded-full ${
                                utilization > 80 ? 'bg-yellow-500' : 'bg-blue-500'
                              }`}
                              style={{ width: `${utilization}%` }}
                            />
                          </div>
                          <span className="text-sm">{utilization.toFixed(0)}%</span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
        </div>
      </div>
    </div>
  );
};

export default ServicesHealthPage;
